import {
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CommonList,
  ConfirmSelectionButton,
  ListSelectionMode,
  SelectionPageHeader,
} from '../../common/components';
import type { Stock } from '../../../types/stock';
import type { ActivityFilters } from '../../activity/types';
import { useStockData } from '../useStockData';
import { useStockMultiSelection } from './useStockMultiSelection';
import type { StockSelectionContext } from '../types';

export default function StockMultiSelectPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id) : null;

  const { data: { stocks, loading, error } } = useStockData({ companyId });

  const selectionContext = location.state as StockSelectionContext;

  const {
    data: { selectedStocksMap, limitError },
    actions: { handleToggleStockSelection, handleClearLimitError },
  } = useStockMultiSelection({
    stocks,
    initialStocks: selectionContext?.activityFilters?.stocks,
  });

  const handleConfirmMultiSelection = () => {
    if (selectedStocksMap.size > 0 && selectionContext?.returnPath) {
      const selectedStocks = Array.from(selectedStocksMap.values());
      
      // Оновлюємо activityFilters з новими складами
      const updatedFilters: ActivityFilters = {
        ...selectionContext.activityFilters,
        stocks: selectedStocks,
      } as ActivityFilters;

      navigate(selectionContext.returnPath, {
        state: {
          activityFilters: updatedFilters,
        },
      });
    }
  };

  const selectedStocksCount = selectedStocksMap.size;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <SelectionPageHeader
        title="Вибір складів"
        companyId={companyId}
        returnPath={selectionContext?.returnPath}
      >
        <ConfirmSelectionButton
          onClick={handleConfirmMultiSelection}
          count={selectedStocksCount}
          disabled={selectedStocksCount === 0}
        />
      </SelectionPageHeader>

      <Alert severity="info" sx={{ mb: 2 }}>
        Виберіть склади для фільтрації активностей (до 5 шт.)
      </Alert>

      {limitError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={handleClearLimitError}
        >
          {limitError}
        </Alert>
      )}

      {/* List */}
      <Box sx={{ mt: 3 }}>
        {stocks.length === 0 ? (
          <Alert severity="info">
            Складів не знайдено. Створіть склади перед їх вибором.
          </Alert>
        ) : (
          <CommonList<Stock>
            items={stocks}
            toListItem={(item) => ({
              id: item.id,
              primary: item.name,
            })}
            selectionMode={ListSelectionMode.MULTIPLE_SELECTION}
            selectedItemIds={Array.from(selectedStocksMap.keys())}
            onToggleSelect={handleToggleStockSelection}
            emptyMessage="Складів не знайдено"
          />
        )}
      </Box>
    </Box>
  );
}
